import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, IconButton, Divider, FormInstance, Toggle, Grid, Row, Col } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import Textarea from '@/components/Textarea';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { PointType, UpsertPointType } from '@/models/point-type.model';
import { upsertPointType } from '@/services/point-type.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const schema = Schema.Model<UpsertPointType>({
  key: Schema.Types.StringType()
    .isRequired('This field is required.')
    .pattern(/^[A-Za-z][A-Za-z0-9_]{1,49}$/, 'Letters, digits and underscore; must start with a letter (2-50 chars).'),
  name: Schema.Types.StringType().isRequired('This field is required.').maxLength(100),
  description: Schema.Types.StringType().maxLength(1000),
  iconUrl: Schema.Types.StringType(),
  isActive: Schema.Types.BooleanType(),
});

const emptyModel: UpsertPointType = {
  id: undefined,
  key: '',
  name: '',
  description: undefined,
  iconUrl: undefined,
  isActive: true,
};

interface PointTypeFormProps {
  formObject?: PointType;
  pageState: PageState;
}

const PointTypeForm: React.FC<PointTypeFormProps> = ({ formObject, pageState }) => {
  const [formValue, setFormValue] = useState<UpsertPointType>(emptyModel);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if (pageState == PageState.Edit && formObject) {
      setFormValue({
        id: formObject.id,
        key: formObject.key,
        name: formObject.name,
        description: formObject.description,
        iconUrl: formObject.iconUrl,
        isActive: formObject.isActive,
      });
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject, pageState]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await upsertPointType({
        ...formValue,
        description: formValue.description || undefined,
        iconUrl: formValue.iconUrl || undefined,
      });
      if (result.success) {
        toastSuccessMessage('Point type saved');
        if (pageState == PageState.New && result.result?.id) {
          nav(`/point-types/${result.result.id}`);
        } else {
          nav('/point-types');
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Form name="upsert-point-type" fluid ref={formRef}
        onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid fluid>
          <Row>
            <Col span={{ xs: 24, xl: 12 }}>
              <Form.Group>
                <Form.Label>Key</Form.Label>
                <Form.Control name="key" disabled={pageState == PageState.Edit} />
                <Form.HelpText>{pageState == PageState.Edit ? 'Key cannot be changed after creation.' : 'Unique code, e.g. NIKE_COIN. Immutable after creation.'}</Form.HelpText>
              </Form.Group>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control name="description" accepter={Textarea} rows={3} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Icon URL</Form.Label>
                <Form.Control name="iconUrl" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Active</Form.Label>
                <Form.Control name="isActive" accepter={Toggle} />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Form>
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      <Divider />
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav('/point-types')}>Back</IconButton>
        <IconButton icon={<PageNextIcon />} placement="right" appearance="primary" onClick={handleSubmit} loading={loading}>
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default PointTypeForm;
